import { useEffect, useMemo, useRef, useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import SideModal from "/src/components/modal/SideModal/SideModal";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import Button from "/src/components/UI/Button/Button";
import Field from "/src/components/UI/Field/Field";
import TextInput from "/src/components/UI/TextField/TextInput";
import useUnitLabels from "/src/hooks/useUnitLabels";
import storage from "/src/utils/storage";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import ConversationDropzone from "../ConversationDropzone/ConversationDropzone";
import ConversationCompletedService from "../../services/ConversationCompletedService";
import "./AudioAnalysisModal.css";

const AUDIO_ACCEPT = { "audio/*": [".mp3", ".wav", ".m4a", ".aac", ".ogg", ".oga", ".opus", ".flac", ".webm"] };
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

const formatSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const readErrorMessage = (error) => {
    try {
        const body = typeof error?.body === "string" ? JSON.parse(error.body) : null;
        if (typeof body?.detail === "string") return body.detail;
    } catch (e) {
    }
    return error?.status ? `The server answered with an error (${error.status}).` : "The server could not be reached.";
};

const STEPS = [
    { key: "transcribing", label: "Transcribing the audio" },
    { key: "analyzing", label: "Analyzing the conversation" },
    { key: "done", label: "Analysis complete" },
];

const AudioAnalysisModal = NiceModal.create(() => {
    const modal = useModal();
    const { onClose } = useModalHelper(modal);
    const navigate = useNavigate();
    const toast = useToastDispatchContext();
    const { unitLabels } = useUnitLabels();
    const currentUser = storage("local").get("user");

    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [unit, setUnit] = useState(null);
    const [agent, setAgent] = useState(currentUser?.email || "");
    const [phase, setPhase] = useState("idle"); // idle | transcribing | analyzing | done | error
    const [callId, setCallId] = useState(null);
    const [result, setResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const cancelledRef = useRef(false);

    const unitOptions = useMemo(
        () => unitLabels.map((item) => ({ value: item.unit_id, label: item.label })),
        [unitLabels]
    );

    useEffect(() => () => { cancelledRef.current = true; }, []);

    useEffect(() => {
        if (!file) return undefined;
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const isBusy = phase === "transcribing" || phase === "analyzing";
    const canSubmit = Boolean(file && unit && agent.trim()) && !isBusy;

    const reset = () => {
        setFile(null);
        setPhase("idle");
        setCallId(null);
        setResult(null);
        setErrorMessage("");
    };

    const fail = (message) => {
        setErrorMessage(message);
        setPhase("error");
        toast.error("Analysis failed", message);
    };

    const waitForAnalysis = async (newCallId) => {
        const startedAt = Date.now();
        while (!cancelledRef.current && Date.now() - startedAt < POLL_TIMEOUT_MS) {
            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
            if (cancelledRef.current) return;

            const [response, error] = await ConversationCompletedService.getConversation({ id: newCallId });
            if (error || !response?.data) continue;

            if (response.data.status === "done") {
                setResult({ score: response.data.score });
                setPhase("done");
                toast.success("Analysis complete", "The audio has been transcribed and analyzed.");
                return;
            }
        }
        if (!cancelledRef.current) fail("The analysis is taking too long. Check the conversation list in a few minutes.");
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setErrorMessage("");
        setPhase("transcribing");

        const formData = new FormData();
        formData.append("file", file);
        formData.append("payload", JSON.stringify({
            user_id: agent.trim(),
            unit_id: unit.value,
            timestamp: new Date().toISOString(),
            metadata: { call_direction: "test upload", contact_id: file.name },
        }));

        const [response, error] = await ConversationCompletedService.uploadAudioFile(formData);
        if (cancelledRef.current) return;
        if (error || !response?.call_id) return fail(readErrorMessage(error));

        setCallId(response.call_id);
        setPhase("analyzing");
        waitForAnalysis(response.call_id);
    };

    const openConversation = () => {
        onClose();
        navigate("/supervision/conversation/profil/" + callId);
    };

    const currentStepIndex = STEPS.findIndex((step) => step.key === phase);

    return (
        <SideModal id={modal.id} autoClose={false} onClose={onClose} style={{ width: "550px" }} name="audio_analysis_modal">
            <div className="audio-analysis-leave">
                <div className="button-leave" id="close_modal_container" onClick={onClose}>
                    <i id="close_modal" className="fa fa-times" aria-hidden="true"></i>
                </div>
            </div>

            <div className="audio-analysis">
                <h2><i className="fas fa-wave-square"></i> Analyze an audio file</h2>
                <p className="audio-analysis__intro">
                    Drop a call recording: it is transcribed and analyzed exactly like a conversation received by the API.
                </p>

                {!file ? (
                    <ConversationDropzone
                        multiple={false}
                        accept={AUDIO_ACCEPT}
                        label="Drag & drop an audio file or"
                        onFilesAdded={(files) => files[0] && setFile(files[0])}
                    />
                ) : (
                    <div className="audio-analysis__file">
                        <div className="audio-analysis__file-head">
                            <i className="fas fa-file-audio"></i>
                            <div className="audio-analysis__file-name">
                                <strong title={file.name}>{file.name}</strong>
                                <span>{formatSize(file.size)}</span>
                            </div>
                            {!isBusy && phase !== "done" && (
                                <button type="button" className="audio-analysis__remove" title="Remove this file" onClick={reset}>
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                        {previewUrl && <audio controls src={previewUrl} />}
                    </div>
                )}

                {file && phase !== "done" && (
                    <div className="audio-analysis__form">
                        <Field label={<span>Unit <span className="obligatory_field">*</span></span>} title="Unit">
                            <Select
                                classNamePrefix="criteria-select"
                                options={unitOptions}
                                value={unit}
                                onChange={setUnit}
                                isDisabled={isBusy}
                                placeholder="Select the unit of the call..."
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 20000 }) }}
                            />
                        </Field>
                        <p className="audio-analysis__hint">
                            The unit decides which scorecard (criteria) is used and the language of the transcription.
                        </p>
                        <Field label={<span>Agent <span className="obligatory_field">*</span></span>} title="Agent">
                            <TextInput
                                className="full-width"
                                type="email"
                                value={agent}
                                disabled={isBusy}
                                placeholder="agent email"
                                onChange={(event) => setAgent(event.target.value)}
                            />
                        </Field>
                    </div>
                )}

                {(isBusy || phase === "done") && (
                    <ol className="audio-analysis__steps">
                        {STEPS.map((step, index) => {
                            const state = phase === "done" || index < currentStepIndex ? "is-done" : index === currentStepIndex ? "is-active" : "";
                            return (
                                <li key={step.key} className={state}>
                                    <span className="audio-analysis__step-icon">
                                        {state === "is-done" ? <i className="fas fa-check"></i> : state === "is-active" ? <i className="fas fa-spinner fa-spin"></i> : index + 1}
                                    </span>
                                    {step.label}
                                </li>
                            );
                        })}
                    </ol>
                )}

                {phase === "transcribing" && (
                    <p className="audio-analysis__hint">Long recordings can take a few minutes. Keep this window open.</p>
                )}

                {phase === "error" && <div className="audio-analysis__error">{errorMessage}</div>}

                {phase === "done" && (
                    <div className="audio-analysis__result">
                        {result?.score != null && <span className="audio-analysis__score">Score {result.score}</span>}
                        <div className="audio-analysis__actions">
                            <Button className="validate" label="Open the conversation" onClick={openConversation} />
                            <Button className="validate" label="Analyze another file" onClick={() => { reset(); setUnit(null); }} />
                        </div>
                    </div>
                )}

                {file && phase !== "done" && (
                    <div className="audio-analysis__actions">
                        <Button
                            className="validate"
                            disabled={!canSubmit}
                            onClick={handleSubmit}
                            label={isBusy ? "In progress..." : "Transcribe & analyze"}
                        />
                    </div>
                )}
            </div>
        </SideModal>
    );
});

export default AudioAnalysisModal;
